import { Shield, Lock, Eye, Globe, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPolicy() {
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="h-screen overflow-y-auto bg-gray-50 selection:bg-[#CEF12F] selection:text-black">
      {/* Header / Hero Section */}
      <header className="relative bg-gradient-to-br from-[#166B93] to-[#0f4c6a] text-white pt-20 pb-24 px-6 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#CEF12F] opacity-10 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#FF9142] opacity-10 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3 pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6 animate-fade-in">
            <Shield className="w-4 h-4 text-[#CEF12F]" />
            <span className="text-sm font-medium tracking-wide">Secure & Confidential</span>
          </div>
          
          <h1 className="heading-primary text-white mb-6">
            Privacy Policy
          </h1>
          
          <p className="body-large text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Your trust is our priority. We are committed to protecting your personal information and ensuring a secure medical journey.
          </p>

          <div className="mt-8 flex justify-center">
             <div className="inline-block bg-white/10 backdrop-blur-md rounded-xl px-6 py-3 border border-white/20">
               <p className="body-small text-blue-100">
                 Effective Date: <span className="font-semibold text-white">{currentDate}</span>
               </p>
             </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 -mt-16 pb-32 relative z-20">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-6 md:p-12 space-y-16">
              
              {/* Introduction */}
              <div className="space-y-6">
                <p className="heading-tertiary text-gray-800 leading-relaxed font-medium">
                  <span className="font-bold text-[#166B93]">JEMA Services</span> (“JEMA,” “we,” “our,” or “us”) respects your privacy and is committed to protecting your personal information. This <span className="font-semibold text-gray-900">Privacy Policy</span> explains how information collected through the JEMA intake form is used, stored, and protected.
                </p>
                <p className="body-base text-gray-600 leading-relaxed">
                  By submitting information through the JEMA intake form, you acknowledge and agree to the practices described below.
                </p>
              </div>

              {/* Section: Information We Collect */}
              <section className="space-y-8">
                <div className="flex items-center gap-4 border-b border-gray-100 pb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#166B93]/10 flex items-center justify-center text-[#166B93]">
                    <Eye className="w-6 h-6" />
                  </div>
                  <h2 className="heading-secondary text-gray-900">Information We Collect</h2>
                </div>
                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100/50 hover:border-[#166B93]/20 transition-colors duration-300">
                   <p className="body-base text-gray-700 mb-6 font-medium">Through the intake form, we may collect the following types of information:</p>
                   <ul className="space-y-4">
                     {[
                       { title: "Personal Details", desc: "Name, email address, phone number, country of residence." },
                       { title: "Health Information", desc: "Health-related data voluntarily provided by you for coordination purposes." },
                       { title: "Files & Documents", desc: "Photos, documents, or lab results you choose to upload." },
                       { title: "Preferences", desc: "Information related to your treatment interests, preferences, or timing." }
                     ].map((item, idx) => (
                       <li key={idx} className="flex items-start gap-4 group">
                         <div className="w-2 h-2 rounded-full bg-[#FF9142] mt-2.5 flex-shrink-0 group-hover:scale-125 transition-transform" />
                         <span className="body-base text-gray-600">
                           <strong className="text-gray-900 font-semibold">{item.title}:</strong> {item.desc}
                         </span>
                       </li>
                     ))}
                   </ul>
                   <p className="body-small text-gray-500 italic mt-8 pt-6 border-t border-gray-200">
                     * You are not required to submit any information you are uncomfortable sharing; however, incomplete information may limit our ability to assist you.
                   </p>
                </div>
              </section>

              {/* Section: How We Use Your Information */}
              <section className="space-y-8">
                 <div className="flex items-center gap-4 border-b border-gray-100 pb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#CEF12F]/20 flex items-center justify-center text-[#9db826]">
                    <Lock className="w-6 h-6" />
                  </div>
                  <h2 className="heading-secondary text-gray-900">How We Use Your Information</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  {[
                    "Evaluate your request for medical coordination services",
                    "Match your case with appropriate providers or partners",
                    "Communicate with you regarding next steps, consultations, or follow-ups",
                    "Improve our intake, coordination, and platform services"
                  ].map((item, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                      <div className="w-8 h-1 bg-[#166B93] rounded-full mb-4"></div>
                      <p className="body-base text-gray-700 font-medium leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50/50 p-6 rounded-2xl text-blue-900 text-sm border border-blue-100 flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#166B93] flex-shrink-0 mt-0.5" />
                  <p><span className="font-bold text-[#166B93]">Important Note:</span> JEMA does not provide medical care, medical advice, or diagnoses.</p>
                </div>
              </section>

              {/* Section: Information Sharing */}
              <section className="space-y-8">
                 <div className="pl-6 border-l-4 border-[#166B93]">
                    <h2 className="heading-secondary text-gray-900 mb-2">Information Sharing</h2>
                    <p className="body-base text-gray-500">How we share your data with trusted partners.</p>
                 </div>
                 
                 <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                   <div className="p-6 bg-gray-50 border-b border-gray-100">
                      <p className="body-base text-gray-700 font-medium">Your information may be shared only with:</p>
                   </div>
                   <div className="divide-y divide-gray-100">
                      {[
                        "Verified healthcare providers or partners relevant to your request",
                        "Internal JEMA personnel responsible for coordination and support",
                        "Service providers supporting our platform operations (e.g., secure hosting, communications)"
                      ].map((item, idx) => (
                         <div key={idx} className="flex items-center gap-4 p-5 hover:bg-gray-50/80 transition-colors">
                            <div className="w-8 h-8 rounded-full bg-[#166B93]/10 flex items-center justify-center flex-shrink-0 text-[#166B93] font-bold text-xs">
                              {idx + 1}
                            </div>
                            <span className="body-base text-gray-600">{item}</span>
                         </div>
                      ))}
                   </div>
                 </div>

                 <div className="p-6 bg-gradient-to-r from-[#166B93]/5 to-[#CEF12F]/10 rounded-2xl border border-[#166B93]/10">
                    <p className="body-base text-gray-800 font-medium text-center max-w-2xl mx-auto leading-relaxed">
                      We <span className="text-[#FF9142] font-bold">do not sell</span> your personal or health information to third parties. Information is shared strictly on a need-to-know basis and only for coordination purposes.
                    </p>
                 </div>
              </section>

              {/* Data Security & Retention */}
              <div className="grid md:grid-cols-2 gap-8">
                 <section className="space-y-4 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                    <h2 className="heading-tertiary text-gray-900 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#166B93]" />
                      Data Security
                    </h2>
                    <p className="body-base text-gray-600 leading-relaxed">
                      We take reasonable administrative, technical, and organizational measures to protect your information against unauthorized access, loss, or misuse. However, no system can be guaranteed to be 100% secure.
                    </p>
                 </section>
                 <section className="space-y-4 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                    <h2 className="heading-tertiary text-gray-900 flex items-center gap-2">
                       <Lock className="w-5 h-5 text-[#FF9142]" />
                       Data Retention
                    </h2>
                    <p className="body-base text-gray-600 leading-relaxed">
                      We retain intake information only as long as necessary to fulfill coordination purposes, comply with legal obligations, or support legitimate business operations. You may request deletion of your information at any time.
                    </p>
                 </section>
              </div>

              {/* Your Rights & International Users */}
              <section className="space-y-8 pt-8 border-t border-gray-100">
                 <div className="grid md:grid-cols-2 gap-12">
                   <div>
                      <h3 className="heading-tertiary text-gray-900 mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#166B93]" />
                        International Users
                      </h3>
                      <p className="body-small text-gray-600 leading-relaxed text-justify">
                        By submitting the intake form, you acknowledge that your information may be processed and stored in countries other than your own, including Colombia and the United States, where data protection laws may differ.
                      </p>
                   </div>
                   <div>
                       <h3 className="heading-tertiary text-gray-900 mb-4 flex items-center gap-2">
                          <Eye className="w-5 h-5 text-[#166B93]" />
                          Your Rights
                       </h3>
                       <p className="body-small text-gray-600 mb-4">Depending on your jurisdiction, you may have the right to:</p>
                       <ul className="space-y-3">
                         {["Request access to your information", "Request correction or deletion of your information", "Withdraw consent for future communications"].map((right, idx) => (
                           <li key={idx} className="flex items-center gap-3 body-small text-gray-700">
                             <div className="w-1.5 h-1.5 bg-[#CEF12F] rounded-full shadow-sm ring-1 ring-gray-200"></div>
                             {right}
                           </li>
                         ))}
                       </ul>
                   </div>
                 </div>
              </section>

              {/* Disclaimer */}
              <div className="bg-orange-50/80 rounded-2xl p-8 border border-orange-100/50">
                <h3 className="heading-tertiary text-[#FF9142] mb-3">No Medical Advice Disclaimer</h3>
                <p className="body-small text-orange-900/80 leading-relaxed">
                  Information submitted through the intake form does not create a doctor-patient relationship. JEMA acts solely as a medical coordination platform and does not provide medical advice or treatment.
                </p>
              </div>

               {/* Contact & Updates */}
               <section className="space-y-8 pt-8 border-t border-gray-200">
                  <div className="text-center max-w-2xl mx-auto">
                    <h2 className="heading-secondary text-gray-900 mb-8">Contact Information</h2>
                    <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100 hover:border-[#166B93]/30 transition-colors">
                        <p className="heading-tertiary text-[#166B93] mb-2">JEMA Services</p>
                        <p className="body-small text-gray-500 mb-6">Medical Coordination & Support</p>
                        
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                          <a href="mailto:contact@jemaservices.com.co" className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gray-50 hover:bg-[#166B93]/5 text-gray-700 hover:text-[#166B93] transition-all border border-transparent hover:border-[#166B93]/10">
                            <Mail className="w-4 h-4" />
                            <span className="font-medium">contact@jemaservices.com.co</span>
                          </a>
                          
                          <a href="https://jemaservices.com.co" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gray-50 hover:bg-[#166B93]/5 text-gray-700 hover:text-[#166B93] transition-all border border-transparent hover:border-[#166B93]/10">
                            <Globe className="w-4 h-4" />
                            <span className="font-medium">jemaservices.com.co</span>
                          </a>
                        </div>
                    </div>
                  </div>
                  <p className="text-center text-xs text-gray-400 mt-12">
                    JEMA may update this Privacy Policy from time to time. Updates will be posted on our website, and continued use of the intake form constitutes acceptance of any changes.
                  </p>
               </section>

            </div>
        </div>
      </main>

      {/* Footer / Floating Action */}
      <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none flex justify-center pb-8 pt-12 bg-gradient-to-t from-gray-50 via-gray-50/90 to-transparent">
         <div className="pointer-events-auto">
            <Button 
              className="rounded-full bg-[#166B93] hover:bg-[#135a7c] text-white px-8 h-12 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
              onClick={() => window.close()}
            >
              Close & Return to Intake
            </Button>
         </div>
      </div>
    </div>
  );
}
